import { useState, useEffect } from 'react';
import { cmApi } from '../api.js';
import { useClient } from '../ClientContext.jsx';
import { fmtCurrency } from '../format.js';

const EMPTY_FORM = { month: '', market: '', channel: '', visual_type: '', category_name: '', brand_name: '', producer_name: '', spend: '' };

// <input type="month"> works in "YYYY-MM"; the backend stores/returns a full DATE.
const toMonthInput = (date) => (date ? date.slice(0, 7) : '');
const toMonthDate  = (month) => (month ? `${month}-01` : '');

export default function ActualDataPage() {
  const { clientId } = useClient();
  const [filterOptions, setFilterOptions] = useState(null);
  const [month, setMonth] = useState('');
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    cmApi.getFilters(clientId).then(setFilterOptions).catch(e => setErr(e.message));
  }, [clientId]);

  const refreshEntries = (m) => {
    if (!clientId || !m) { setEntries([]); return; }
    cmApi.listActual(clientId, toMonthDate(m)).then(d => setEntries(d.entries)).catch(e => setErr(e.message));
  };

  useEffect(() => { refreshEntries(month); }, [clientId, month]);

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      month: toMonthInput(entry.month), market: entry.market, channel: entry.channel,
      visual_type: entry.visual_type, category_name: entry.category_name,
      brand_name: entry.brand_name, producer_name: entry.producer_name || '', spend: entry.spend,
    });
  };

  const resetForm = () => { setEditingId(null); setForm(EMPTY_FORM); };

  const save = async () => {
    setErr(null); setSaving(true);
    try {
      const body = { ...form, month: toMonthDate(form.month), producer_name: form.producer_name || null, spend: Number(form.spend) };
      if (editingId) {
        await cmApi.updateActual(clientId, editingId, body);
      } else {
        await cmApi.createActual(clientId, body);
      }
      resetForm();
      refreshEntries(month || form.month);
      setMonth(form.month || month);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setErr(null);
    try {
      await cmApi.deleteActual(clientId, id);
      refreshEntries(month);
    } catch (e) {
      setErr(e.message);
    }
  };


  const field = (key, label, opts) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#8C94A0' }}>
      {label}
      {opts ? (
        <select value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={{ background: '#FFFFFF', color: '#171A1F', border: '1px solid #E3E2DD', borderRadius: 8, padding: '6px 8px', fontSize: 12 }}>
          <option value="">Select…</option>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          type={key === 'month' ? 'month' : key === 'spend' ? 'number' : 'text'}
          style={{ background: '#FFFFFF', color: '#171A1F', border: '1px solid #E3E2DD', borderRadius: 8, padding: '6px 8px', fontSize: 12 }} />
      )}
    </label>
  );

  if (!clientId) return <p className="o-state">Select a client to enter actual spend.</p>;

  return (
    <div style={{ padding: 20 }}>
      <h4>{editingId ? 'Edit Actual Spend Entry' : 'Add Actual Spend Entry'}</h4>
      {err && <div style={{ color: '#A32B2B', marginBottom: 10 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        {field('month', 'Month')}
        {field('market', 'Market', filterOptions?.markets)}
        {field('channel', 'Channel', filterOptions?.channels)}
        {field('visual_type', 'Visual Type', filterOptions?.visual_types)}
        {field('category_name', 'Category', filterOptions?.categories)}
        {field('brand_name', 'Brand', filterOptions?.brands)}
        {field('producer_name', 'Producer (optional)', filterOptions?.producers)}
        {field('spend', 'Spend (USD)')}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={save} disabled={saving} style={{
          background: '#171A1F', color: '#F5F5F2', border: 'none', borderRadius: 8,
          padding: '7px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1,
        }}>{saving ? 'Saving…' : editingId ? 'Update' : 'Add'}</button>
        {editingId && <button onClick={resetForm} style={{ background: 'none', border: 'none', color: '#8C94A0', cursor: 'pointer' }}>Cancel</button>}
      </div>

      <h4 style={{ marginTop: 30 }}>Entries</h4>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#8C94A0', marginBottom: 12, maxWidth: 200 }}>
        Filter by month
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          style={{ background: '#FFFFFF', color: '#171A1F', border: '1px solid #E3E2DD', borderRadius: 8, padding: '6px 8px', fontSize: 12 }} />
      </label>
      <table className="metrics-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Market</th><th>Channel</th><th>Visual Type</th><th>Category</th><th>Brand</th><th>Producer</th><th className="num">Spend</th><th></th>
          </tr>
        </thead>
        <tbody>
          {entries.map(e => (
            <tr key={e.id}>
              <td>{e.market}</td><td>{e.channel}</td><td>{e.visual_type}</td><td>{e.category_name}</td>
              <td>{e.brand_name}</td><td>{e.producer_name || '—'}</td>
              <td className="num">{fmtCurrency(e.spend)}</td>
              <td>
                <button onClick={() => startEdit(e)} style={{ background: 'none', border: 'none', color: '#171A1F', cursor: 'pointer', marginRight: 8 }}>Edit</button>
                <button onClick={() => remove(e.id)} style={{ background: 'none', border: 'none', color: '#A32B2B', cursor: 'pointer' }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* The old Calibration Preview lived here. It computed a ratio with its own SQL,
          separate from what was actually applied, so it could show a number that was never
          used. The Calibration Factors tab replaces it and shows the real figures with the
          months behind each one. */}
      <p className="o-note" style={{ marginTop: 24 }}>
        These entries are one of four actual-spend sources. To see the factors they produce,
        and the months behind each, use the Calibration Factors tab.
      </p>
    </div>
  );
}
